/**
 * entity-detail-api.ts — wire→view-model mappers for the T2 entity-detail
 * screens (Story V10-J2).
 *
 * Live wire sources (all verified as-built):
 *   GET /customers                          — header lookup (subject_id → row)
 *   GET /policies?subject_id|carrier_uid    — Policen tabs
 *   GET /policies/:id + /versions           — Police detail (+ current premium)
 *   GET /invoices?subject_type&subject_id   — Rechnungen tab
 *   GET /communications?bucket&subject…     — Kommunikation tab
 *   GET /persons|firms/:id/relationships    — Beziehungen tab
 *
 * Mapping decisions (display-only; the api decides, the UI renders):
 *   - Policy view status: active/in_mutation→aktiv · renewal_pending→renewal ·
 *     draft→sistiert · cancelled/expired→gekuendigt (4-value T2 display map).
 *   - praemie: the GET /policies list rows DO carry the open version's
 *     `premium_gross` (Story GAP-B4) and it is parsed here; null when the wire
 *     omits it or the `policen.praemie` gate stripped it, so a denied caller sees
 *     a plain '-' and never a fabricated 0. The Police DETAIL still resolves its
 *     own premium from /versions and passes it as `opts.praemie`.
 *   - versicherer: carrier_uid on the wire; display name resolved as an overlay
 *     from the carriers list (hooks); `versichererId` carries the uid for
 *     navigation.
 *
 * [Source: apps/web/src/features/customers/api/customers-api.ts] (wrapper pattern)
 * [Source: docs/stories/v10-j2-web-wiring-rest.md AC5]
 */

import type {
  InvoiceListItemDto,
  LineOfBusiness,
  PolicyDto,
  PolicyListItemDto,
  PolicyStatus as WirePolicyStatus,
  PolicyVersionDto,
} from '@brokly/shared';
import type {
  CommOverviewBucket,
  CommOverviewItem,
  RelationshipFeedItem,
} from '../../../lib/api-client';
import { dateShortDe, daysUntil } from '../../../lib/format';
import type { Beziehung, Komm, KommKategorie, KundeRechnung, Policy, PolicyStatus } from '../types';

// ── Display maps ──────────────────────────────────────────────────────────────

/** Wire policy status → the 4-value T2 view status (display map only). */
const POLICY_VIEW_STATUS: Record<WirePolicyStatus, PolicyStatus> = {
  draft: 'sistiert',
  active: 'aktiv',
  in_mutation: 'aktiv',
  renewal_pending: 'renewal',
  cancelled: 'gekuendigt',
  expired: 'gekuendigt',
};

/** line_of_business → German display label. */
const SPARTE_LABEL: Record<LineOfBusiness, string> = {
  uvg: 'UVG',
  ktg: 'KTG',
  bu: 'BU',
  leben: 'Leben',
  sach: 'Sach',
  haftpflicht: 'Haftpflicht',
  kranken: 'Kranken',
  other: 'Übrige',
};

export function sparteLabel(lob: LineOfBusiness | null): string {
  return lob ? SPARTE_LABEL[lob] : '-';
}

// ── Policy mappers ────────────────────────────────────────────────────────────

/**
 * The list wire's `premium_gross` (a numeric string) as a number.
 * null for absent / stripped / unparseable, so the cell can render '-'.
 */
function parsePremiumGross(premiumGross: string | null | undefined): number | null {
  if (premiumGross == null || premiumGross.trim() === '') return null;
  const n = Number(premiumGross);
  return Number.isFinite(n) ? n : null;
}

/**
 * One policy wire row → the T2 Policy view-model.
 * praemie: `opts.praemie` (the detail path, resolved from /versions) wins; otherwise
 * the list row's own `premium_gross`, parsed. null when neither is available.
 */
export function mapPolicyDtoToView(
  dto: PolicyDto &
    Partial<
      Pick<
        PolicyListItemDto,
        | 'premium_gross'
        | 'registration_id'
        | 'registration_exception_reason'
        | 'registration_status'
        | 'registration_status_label'
        | 'registration_inherited_from_policy_number'
      >
    >,
  opts: { kundeName?: string; praemie?: number } = {},
): Policy {
  return {
    id: dto.id,
    nr: dto.policy_number,
    kunde: opts.kundeName ?? '',
    kundeId: dto.subject_id,
    // Display overlay swaps the uid for the register name when known.
    versicherer: dto.carrier_uid ?? '-',
    versichererId: dto.carrier_uid ?? undefined,
    sparte: sparteLabel(dto.line_of_business),
    praemie: opts.praemie ?? parsePremiumGross(dto.premium_gross),
    status: POLICY_VIEW_STATUS[dto.status],
    renewalTage: daysUntil(dto.expiry_date) ?? -1,
    beginn: dto.inception_date ? dateShortDe(dto.inception_date) : '-',
    // DMS M12: Vertragsende for the policy-detail Stammdaten card (dms/spec.md
    // §4.5). Optional — `PolicyListRow`/mock rows that never set it render '—'.
    ende: dto.expiry_date ? dateShortDe(dto.expiry_date) : undefined,
    // DMS Part2 P2-4 (WS4 §4.1-§4.3): Zahlungsart + Bezeichnung + the manual
    // Stammdaten fields, carried straight through from the wire (server-resolved,
    // no display logic here — coding-standards §3).
    paymentFrequency: dto.payment_frequency,
    bezeichnung: dto.bezeichnung,
    praemienbefreiung: dto.praemienbefreiung,
    praemienbefreiungSeit: dto.praemienbefreiung_seit,
    vermittlerPersonId: dto.vermittler_person_id,
    bemerkungen: dto.bemerkungen,
    // P2 Placement Slice 2: the Sparte classification, carried straight through from the wire.
    igb2bCode: dto.igb2b_code,
    // P5-S5: the registration fields of the list read (absent on other readers).
    ...(dto.registration_id !== undefined && { registrationId: dto.registration_id }),
    ...(dto.registration_exception_reason !== undefined && {
      registrationExceptionReason: dto.registration_exception_reason,
    }),
    ...(dto.registration_status !== undefined && { registrationStatus: dto.registration_status }),
    ...(dto.registration_status_label !== undefined && {
      registrationStatusLabel: dto.registration_status_label,
    }),
    ...(dto.registration_inherited_from_policy_number !== undefined && {
      registrationInheritedFromPolicyNumber: dto.registration_inherited_from_policy_number,
    }),
  };
}

/** The current version's premium_gross (valid_to IS NULL), as a number (0 when absent). */
export function currentPremiumOf(versions: PolicyVersionDto[]): number {
  const current = versions.find((v) => v.valid_to === null);
  return current?.premium_gross !== null && current?.premium_gross !== undefined
    ? Number(current.premium_gross)
    : 0;
}

/**
 * The open policy_version (`valid_to === null`), or `null` when the chain is
 * empty/has no open row. DMS M12: feeds the policy-detail Stammdaten
 * "Finanzen" card (Stempelabgabe/Nettoprämie) + the "Dokumente öffnen" →
 * `source_document_id` trigger (dms/spec.md §4.5, §M12).
 */
export function currentVersionOf(versions: PolicyVersionDto[]): PolicyVersionDto | null {
  return versions.find((v) => v.valid_to === null) ?? null;
}

// ── Rechnungen (Kunde tab) ────────────────────────────────────────────────────

/** One invoice wire row → the KundeRechnung view-model (Kunde Rechnungen tab). */
export function mapInvoiceToKundeRechnung(dto: InvoiceListItemDto, kundeId: string): KundeRechnung {
  const status = dto.check_status ?? dto.status;
  const toned =
    status === 'released' || status === 'resolved' || status === 'paid'
      ? { tone: 'ok' as const, label: 'Bezahlt/Freigegeben' }
      : status === 'in_review' || status === 'blocked' || status === 'disputed'
        ? { tone: 'decide' as const, label: 'In Klärung' }
        : { tone: 'info' as const, label: 'Offen' };
  return {
    id: dto.id,
    kundeId,
    nr: dto.invoice_number ?? dto.id.slice(0, 8).toUpperCase(),
    datum: dateShortDe(dto.created_at),
    betrag:
      dto.invoiced_premium !== null
        ? Number(dto.invoiced_premium)
        : dto.amount_gross !== null
          ? Number(dto.amount_gross)
          : 0,
    status: toned,
  };
}

// ── Kommunikation (Kunde tab) ─────────────────────────────────────────────────

/** One comm-overview row → the Komm view-model. The bucket IS the server state. */
export function mapCommToKomm(
  item: CommOverviewItem,
  bucket: CommOverviewBucket,
  kundeId: string,
): Komm {
  const status =
    bucket === 'gesendet'
      ? { tone: 'ok' as const, label: 'Gesendet' }
      : bucket === 'ausgang_entwuerfe'
        ? item.task_status === 'awaiting_approval'
          ? { tone: 'decide' as const, label: 'Freigabe' }
          : { tone: 'decide' as const, label: 'Entwurf' }
        : item.task_status === 'open' || item.task_status === 'awaiting_approval'
          ? { tone: 'decide' as const, label: 'Braucht Antwort' }
          : { tone: 'neutral' as const, label: 'Gelesen' };
  // Stefan's 4 buckets: the server folds "Ausgang" (queued drafts) and "Freigabe"
  // (approval-pending) into the one `ausgang_entwuerfe` bucket — split them here by the
  // linked task status (awaiting_approval → Freigabe, else Ausgang). eingang/gesendet map 1:1.
  const kategorie: KommKategorie =
    bucket === 'eingang'
      ? 'eingang'
      : bucket === 'gesendet'
        ? 'gesendet'
        : item.task_status === 'awaiting_approval'
          ? 'freigabe'
          : 'ausgang';
  return {
    id: item.id,
    kundeId,
    betreff: item.subject ?? '(kein Betreff)',
    richtung: item.direction === 'inbound' ? 'ein' : 'aus',
    datum: dateShortDe(item.received_at),
    status,
    kategorie,
    // #7: carry the viewer's effective tier so the row can show "nur Titel" / "Inhalt nicht
    // freigegeben" (mirrors the communication-v2 MessageList lock pattern). The api already
    // stripped the body for reduced tiers; the UI only renders the lock STATE.
    tier: item.your_effective_tier,
    vorgangStatus: item.vorgang_status,
    restrictedToTeam: item.restricted_to_team,
  };
}

/**
 * The effective bucket for a Komm row, used by the client-detail Kommunikation segmented filter.
 * Live rows carry `kategorie` (derived in mapCommToKomm); mock rows don't, so fall back to the
 * direction (ein→eingang, aus→ausgang) — mock mode has no server bucket/task-status to split on.
 */
export function kommKategorie(m: Komm): KommKategorie {
  return m.kategorie ?? (m.richtung === 'ein' ? 'eingang' : 'ausgang');
}

// ── Beziehungen (Kunde tab) ───────────────────────────────────────────────────

const REL_TYPE_LABEL: Record<RelationshipFeedItem['relType'], string> = {
  person_firma: 'Person ↔ Firma',
  person_person: 'Person ↔ Person',
  firma_firma: 'Firma ↔ Firma',
};

/** One relationship-feed row → the Beziehung view-model. */
export function mapRelationshipToBeziehung(item: RelationshipFeedItem, kundeId: string): Beziehung {
  return {
    id: item.id,
    kundeId,
    relatedId: item.counterpart.id,
    relatedName: item.counterpart.displayName ?? '-',
    // Counterparts are CRM subjects (person/firma) → Kunde navigation.
    relatedTyp: 'kunde',
    typ: item.description ?? REL_TYPE_LABEL[item.relType],
    rolle: item.role ?? '',
  };
}
